import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import axios from "axios";
import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/buttons.css';
import '../../styles/popup.css';

import imageUpload from '../general/imageUpload';

import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const EditAnnouncementForm = ({ announcement, onClose }) => {
  const [title, setTitle] = useState(announcement?.announcementTitle || '');
  const [content, setContent] = useState(announcement?.announcement || '');
  const [expiry, setExpiry] = useState(announcement?.expiry ? dayjs(announcement.expiry) : null);
  
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const queryClient = useQueryClient();

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div className="font-poppins" style={{ marginTop: 4 }}>Upload<br />JPG or PNG, max 2MB</div>
    </button>
  );

  useEffect(() => {
    if (!imageFile && announcement?.images?.length > 0) {
      const firstImage = announcement.images[0];
      if (typeof firstImage === "object" && firstImage.key) {
        setImageUrl(firstImage.key);
      } else if (typeof firstImage === "string") {
        setImageUrl(firstImage); 
      }
    }
  }, [announcement, imageFile]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    let imageIds = announcement.images || [];

    if (imageFile) {
      const safeAccountId = announcement.account?._id || announcement.account; 
      const safePropertyId = announcement.property?._id || announcement.property;

      imageIds = await imageUpload(
        [imageFile].map(f => ({ name: f.name, type: f.type, data: f })), 
        "announcement",
        safePropertyId, 
        safeAccountId, 
        ["announcement image"]
      );
    }

    await axios.put(
      `${config.API_URL}/v2/announcement/${announcement._id}`,
      {
        announcementTitle: title,
        announcement: content,
        expiry: expiry ? expiry.toDate().toISOString() : null,
        images: imageIds,
      },
      {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
    );

    queryClient.invalidateQueries(['announcements']);
    toast.success("Announcement updated successfully!");
    onClose();
  } catch (error) {
    console.error("Failed to update announcement", error);
    toast.error("Failed to update announcement.");
  }
};

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-popup" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h2 className="text-2xl text-black">Edit Announcement</h2>
        <form onSubmit={handleSubmit}>
          <div className="announcement-img-upload" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <Upload
              name="announcement"
              listType="picture-card"
              className="announcement-uploader"
              showUploadList={false}
              beforeUpload={(file) => {
                const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                if (!isJpgOrPng) {
                  message.error('You can only upload JPG/PNG file!');
                  return Upload.LIST_IGNORE;
                }
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('Image must be smaller than 2MB!');
                  return Upload.LIST_IGNORE;
                }

                setImageFile(file);
                setImageUrl(URL.createObjectURL(file));
                return false;
              }}
            >
              {imageUrl ? (
                <div style={{ position: 'relative' }}>
                  <img src={imageUrl} alt="announcement" style={{ width: '100%' }} />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageUrl(undefined);
                    }}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(0,0,0,0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      width: 22,
                      height: 22,
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                uploadButton
              )}
            </Upload>
          </div>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
          <TextField
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={6}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date"
              value={expiry}
              onChange={(newVal) => setExpiry(newVal)}
              renderInput={(params) => <TextField {...params} />}
              sx={{ width: '100%', marginBottom: '1rem' }}
            />
          </LocalizationProvider>
          <div className="form-buttons">
            <button type="submit" className="button">Edit Announcement</button>
          </div>
        </form>
      </div>
    </div>
  );

};

export default EditAnnouncementForm;